import React, { useState, useEffect } from 'react';
import { Button } from './button'; // Assuming you have a Button component
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useMediaQuery } from '@/hooks/useMediaQuery'; // Import the hook

interface TruncatedTextProps {
  text: string | null | undefined;
  charLimit?: number;
  buttonClassName?: string;
  textClassName?: string;
  // showToggleButton prop might no longer be needed if component handles responsiveness internally
}

export const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  charLimit = 150, // Default character limit for truncation
  buttonClassName = "text-brand-primary hover:text-brand-primary/80",
  textClassName = "",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isActuallyTruncatable, setIsActuallyTruncatable] = useState(false);

  // Use Tailwind's default 'md' breakpoint (768px) for mobile
  // useMediaQuery will return true if screen width is less than 768px
  const isMobile = useMediaQuery("(max-width: 767px)"); 

  useEffect(() => {
    // Only consider truncating if on mobile and text exceeds limit
    if (isMobile && text && text.length > charLimit) {
      setIsActuallyTruncatable(true);
    } else {
      setIsActuallyTruncatable(false);
      // If not mobile or text isn't long enough, ensure it's always expanded (or rather, not truncated)
      setIsExpanded(true); 
    }
    // Reset expanded state if mobile status changes to ensure correct display
    if (!isMobile) {
        setIsExpanded(true); // Always show full text on desktop
    } else {
        // If switching to mobile and it was previously expanded (desktop), 
        // and it IS truncatable, reset to collapsed view for mobile.
        if (text && text.length > charLimit) setIsExpanded(false); 
    }

  }, [text, charLimit, isMobile]);

  if (!text) {
    return null;
  }

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Display full text if not on mobile, or if expanded, or if not actually truncatable
  const displayText = !isMobile || isExpanded || !isActuallyTruncatable 
                      ? text 
                      : `${text.substring(0, charLimit)}...`;
  
  const shouldShowButton = isMobile && isActuallyTruncatable;

  return (
    <div className={`relative ${textClassName}`}>
      <p className="whitespace-pre-wrap">{displayText}</p>
      {shouldShowButton && (
        <Button
          variant="link"
          onClick={toggleExpand}
          className={`p-0 h-auto mt-1 text-sm flex items-center ${buttonClassName}`}
        >
          {isExpanded ? (
            <>
              See Less <ChevronUp className="ml-1 h-4 w-4" />
            </>
          ) : (
            <>
              See More <ChevronDown className="ml-1 h-4 w-4" />
            </>
          )}
        </Button>
      )}
    </div>
  );
}; 